import { useNavigate } from 'react-router-dom';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import Close from '@mui/icons-material/Close';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import { Button } from '@practium/ui';
import { useAuth } from '@practium/features/auth';
import {
	PaymentResultLayout,
	PaymentResultIcon,
	PaymentResultLoading,
	PaymentFailureDetailsCard,
	PaymentNoticeBanner,
} from '../components/PaymentResult';
import type { PaymentFailureDetails } from '../components/PaymentResult';
import { usePaymentOutcome } from '../hooks/usePaymentOutcome';
import { usePaymentResultReveal } from '../hooks/usePaymentResultReveal';
import styles from './PaymentFailedPageClient.module.css';

const DEFAULT_DECLINE_MESSAGE = 'Transação recusada pela operadora.';

export const PaymentFailedPageClient = () => {
	const navigate = useNavigate();
	const { logout } = useAuth();
	const { subscription, paymentStatus, isResolving, timedOut, isError, isSessionError, checkAgain } =
		usePaymentOutcome();
	const isTerminal = !isResolving && paymentStatus === 'failed' && !!subscription;
	const { phase, landed, standaloneIconRef, finalIconRef } = usePaymentResultReveal(isTerminal);

	const handleLogout = () => {
		logout().then(() => navigate('/login', { replace: true }));
	};

	if (isResolving) {
		return <PaymentResultLoading ref={standaloneIconRef} tone="error" spinning />;
	}

	if (paymentStatus === 'paid') {
		navigate('/billing/success', { replace: true });
		return null;
	}

	if (isTerminal && phase !== 'revealed') {
		return (
			<PaymentResultLoading
				ref={standaloneIconRef}
				tone="error"
				spinning={false}
				icon={phase === 'morphed' ? <Close /> : undefined}
			/>
		);
	}

	if (!isTerminal) {
		return (
			<PaymentResultLayout
				tone="error"
				header={
					<>
						<PaymentResultIcon icon={<CancelOutlined />} tone="error" />
						<div className={styles.headingGroupSpaced}>
							<h1 className={styles.title}>
								{isSessionError
									? 'Sua sessão expirou'
									: isError
										? 'Não foi possível verificar seu pagamento'
										: 'Pagamento em processamento'}
							</h1>
							<p className={styles.subtitle}>
								{isSessionError
									? 'Entre novamente para confirmarmos o status do seu pagamento.'
									: isError
										? 'Não conseguimos consultar o status do seu pagamento agora. Verifique sua conexão e tente novamente.'
										: timedOut
											? 'Ainda estamos confirmando o status do seu pagamento junto ao gateway. Isso pode levar alguns minutos.'
											: 'Estamos confirmando o status do seu pagamento. Aguarde um instante.'}
							</p>
						</div>
					</>
				}
				content={null}
				footer={
					<div className={styles.actions}>
						<Button
							variant="primary"
							fullWidth
							icon={<RefreshOutlined />}
							className={styles.primaryButton}
							onClick={isSessionError ? () => navigate('/login', { replace: true }) : checkAgain}
						>
							{isSessionError ? 'Entrar novamente' : 'Verificar Novamente'}
						</Button>
						<Button
							variant="outline"
							fullWidth
							icon={<LogoutOutlined />}
							className={styles.secondaryButton}
							onClick={handleLogout}
						>
							Sair do Sistema
						</Button>
					</div>
				}
			/>
		);
	}

	const declineMessage = subscription.paymentFailure?.message || DEFAULT_DECLINE_MESSAGE;
	const details: PaymentFailureDetails = {
		status: subscription.paymentFailure ? 'Recusado' : 'Não Aprovado',
		reasons: [declineMessage],
	};

	return (
		<PaymentResultLayout
			tone="error"
			header={
				<>
					<PaymentResultIcon
						ref={finalIconRef}
						icon={<Close />}
						tone="error"
						animateReveal={false}
						bare={!landed}
						ringed
					/>
					<div className={styles.headingGroup}>
						<h1 className={styles.title}>Pagamento não Aprovado!</h1>
						<p className={styles.subtitle}>
							Não conseguimos processar sua transação. Nenhuma cobrança foi realizada. Por favor,
							verifique os seus dados e tente novamente.
						</p>
					</div>
				</>
			}
			content={
				<>
					<PaymentFailureDetailsCard details={details} />
					<PaymentNoticeBanner icon={<WarningAmberOutlined />} tone="error">
						{declineMessage}
					</PaymentNoticeBanner>
				</>
			}
			footer={
				<>
					<div className={styles.actions}>
						<Button
							variant="primary"
							fullWidth
							icon={<ArrowBackOutlined />}
							className={styles.primaryButton}
							onClick={() => navigate('/checkout/pagamento')}
						>
							Tentar Novamente
						</Button>
						<Button
							variant="outline"
							fullWidth
							icon={<PhoneOutlined />}
							className={styles.secondaryButton}
							onClick={() => {
								globalThis.location.href = 'mailto:suporte@practium.com';
							}}
						>
							Entrar em Contato
						</Button>
					</div>
					<p className={styles.helpText}>
						Precisa de ajuda? Entre em contato com suporte@practium.com
					</p>
				</>
			}
		/>
	);
};
